import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, LoaderIcon, AlertTriangle, X, Package } from 'lucide-react';
import { callWFAPI, loadRentalsToLocalStorage } from '../utils/helper';
import { useEmail } from '../contexts/EmailContext';
export function RentalSummary({
  selections,
  subtotal,
  discount,
  tax,
  total,
  onCaliforniaAddressChange,
  prices,
  onRentalSubmitted,
  discountSelections,
  isGen2Mode = false
}) {
  const {
    email: globalEmail,
    setEmail: setGlobalEmail
  } = useEmail();
  const initialCustomerInfo = {
    name: '',
    phone: '',
    email: globalEmail || '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    eventName: '',
    eventVenueCityState: '',
    proposedEventDate: '',
    alternateDate: '',
    notes: '',
    preferredTechnician: ''
  };
  const [customerInfo, setCustomerInfo] = useState(initialCustomerInfo);
  const [showOwnEquipmentModal, setShowOwnEquipmentModal] = useState(false);
  const [ownEquipmentAcknowledged, setOwnEquipmentAcknowledged] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showPreviousRentalsModal, setShowPreviousRentalsModal] = useState(false);
  const [previousRentalsCount, setPreviousRentalsCount] = useState(0);
  const [showNoHeadGearModal, setShowNoHeadGearModal] = useState(false);
  const [noHeadGearConfirmed, setNoHeadGearConfirmed] = useState(false);
  const [technicianOptions, setTechnicianOptions] = useState<Array<{
    id: string;
    name: string;
  }>>([]);
  useEffect(() => {
    // Load technicians from database
    const loadTechnicians = async () => {
      try {
        const data = {
          appname: 'tkd-score',
          reportname: 'All_Technicians'
        };
        const resp = await callWFAPI(0, 'zohoCreator', data);
        const loadedTechnicians = resp.map((item: any) => ({
          id: item.ID,
          name: item.Name
        }));
        setTechnicianOptions(loadedTechnicians);
      } catch (error) {
        console.error('Error loading technicians:', error);
        setTechnicianOptions([]);
      }
    };
    loadTechnicians();
    // Listen for storage changes (when admin updates the list)
    const handleTechnicianUpdate = () => {
      loadTechnicians();
    };
    window.addEventListener('techniciansUpdated', handleTechnicianUpdate);
    return () => {
      window.removeEventListener('techniciansUpdated', handleTechnicianUpdate);
    };
  }, []);
  useEffect(() => {
    if (globalEmail) {
      if (!customerInfo.email) {
        setCustomerInfo(prev => ({
          ...prev,
          email: globalEmail
        }));
      }
    }
  }, [globalEmail]);
  // Check email for existing rentals when email field is filled
  const checkEmailForRentals = async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    // Don't check if already set as global email
    if (email.toLowerCase() === globalEmail?.toLowerCase()) return;
    setIsCheckingEmail(true);
    try {
      // Set the global email
      setGlobalEmail(email.toLowerCase());
    } catch (error) {
      console.error('Error checking email for rentals:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const hasEquipmentSelections = selections.gen3Body > 0 || selections.gen3Head > 0 || selections.gen3JudgesOnly > 0 || selections.fullGen3Ring > 0;
  const hasSelections = hasEquipmentSelections || selections.requireTech;
  const technicianOnlySelected = selections.requireTech && !hasEquipmentSelections;
  const hasFullRingWithoutHead = selections.fullGen3Ring > 0 && selections.gen3Head === 0;
  // Tech is required for Gen 3, optional for Gen 2
  const techRequired = !isGen2Mode;
  useEffect(() => {
    const address = customerInfo.address.toLowerCase();
    const city = customerInfo.city.toLowerCase();
    const state = customerInfo.state.toLowerCase();
    const isCaliforniaAddress = state === 'california' || state === 'ca' || city.includes('california') || city.includes(' ca ') || address.includes('california') || address.includes(' ca ');
    onCaliforniaAddressChange(isCaliforniaAddress);
  }, [customerInfo.address, customerInfo.city, customerInfo.state, onCaliforniaAddressChange]);
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
    // Check for existing rentals when email is entered
    if (name === 'email' && value && /\S+@\S+\.\S+/.test(value)) {
      // Debounce the check
      const timeoutId = setTimeout(() => {
        checkEmailForRentals(value);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!customerInfo.name.trim()) newErrors.name = 'Name is required';
    if (!customerInfo.phone.trim()) newErrors.phone = 'Phone is required';
    if (!customerInfo.email.trim()) newErrors.email = 'Email is required';else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) newErrors.email = 'Email is invalid';
    // Validate that at least one date is provided and is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const proposedDate = customerInfo.proposedEventDate ? new Date(customerInfo.proposedEventDate) : null;
    const alternateDate = customerInfo.alternateDate ? new Date(customerInfo.alternateDate) : null;
    if (!proposedDate && !alternateDate) {
      newErrors.proposedEventDate = 'At least one event date is required';
      newErrors.alternateDate = 'At least one event date is required';
    } else {
      // Check if at least one date is in the future
      const proposedIsFuture = proposedDate && proposedDate >= today;
      const alternateIsFuture = alternateDate && alternateDate >= today;
      if (!proposedIsFuture && !alternateIsFuture) {
        if (proposedDate && proposedDate < today) {
          newErrors.proposedEventDate = 'Date must be in the future';
        }
        if (alternateDate && alternateDate < today) {
          newErrors.alternateDate = 'Date must be in the future';
        }
        if (!proposedDate && alternateDate && alternateDate < today) {
          newErrors.alternateDate = 'At least one future date is required';
        }
        if (!alternateDate && proposedDate && proposedDate < today) {
          newErrors.proposedEventDate = 'At least one future date is required';
        }
      }
    }
    // Tech is only required for Gen 3
    if (techRequired && !selections.requireTech) {
      newErrors.requireTech = 'On-site technician is required for Gen 3 rentals';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const saveRentalToLocalStorage = () => {
    // Removed localStorage logic - rentals are now only saved to database
  };
  const proceedWithSubmission = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (!globalEmail && customerInfo.email) {
        setGlobalEmail(customerInfo.email);
      }
      const discountNotes = [];
      if (discountSelections.collegiate) {
        discountNotes.push('Collegiate Discount Requested (10%)');
      }
      if (discountSelections.usatSanctioned) {
        discountNotes.push('USAT Sanctioned Event Discount Requested (15%)');
      }
      if (isGen2Mode) {
        discountNotes.push('⚠️ GEN 2 EQUIPMENT RENTAL - Not eligible for USAT sanctioned events');
      }
      const notesArray = [customerInfo.notes, discountNotes.join('; ')];
      if (technicianOnlySelected) {
        notesArray.push('Customer has own Gen 3 Daedo equipment - approval required for sanctioned event');
      }
      if (hasFullRingWithoutHead && noHeadGearConfirmed) {
        notesArray.push('Customer confirmed: No e-head gear needed');
      }
      const combinedNotes = notesArray.filter(Boolean).join('\n\n');
      // Format phone number: remove all non-digits, then add +1 if no country code
      const formatPhoneNumber = (phone: string): string => {
        // Remove all non-digit characters except plus sign at the beginning
        let cleaned = phone.replace(/[^\d+]/g, '');
        // If it starts with +, keep it as is (already has country code)
        if (cleaned.startsWith('+')) {
          return cleaned;
        }
        // Remove any plus signs that aren't at the beginning
        cleaned = cleaned.replace(/\+/g, '');
        // Add +1 country code if not present
        return '+1' + cleaned;
      };
      // Ensure dates are in proper YYYY-MM-DD format
      const formatDateForDatabase = (dateString: string): string => {
        if (!dateString) return '';
        // Date input gives us YYYY-MM-DD format, ensure it's valid
        const date = new Date(dateString + 'T00:00:00');
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const zohoData = {
        Name: customerInfo.name,
        Phone_Number: formatPhoneNumber(customerInfo.phone),
        Email: customerInfo.email,
        Shipping_Address: {
          district_city: customerInfo.city || '',
          address_line_1: customerInfo.address || '',
          address_line_2: customerInfo.address2 || '',
          state_province: customerInfo.state || '',
          postal_code: customerInfo.zip || ''
        },
        Event_Name: customerInfo.eventName || '',
        Event_Venue_City_State: customerInfo.eventVenueCityState || '',
        Proposed_Event_Date: formatDateForDatabase(customerInfo.proposedEventDate),
        Alternate_Date: formatDateForDatabase(customerInfo.alternateDate),
        Notes: combinedNotes || '',
        Internal_Notes: customerInfo.notes ? `Customer Notes:\n${customerInfo.notes}` : '',
        Technician: customerInfo.preferredTechnician || '',
        Gen3_Body_Quantity: selections.gen3Body,
        Gen3_Head_Quantity: selections.gen3Head,
        Gen3_Judges_Only_Quantity: selections.gen3JudgesOnly,
        Full_Gen3_Ring_Quantity: selections.fullGen3Ring,
        Requires_Technician: selections.requireTech ? 'true' : 'false',
        Number_Of_Days: selections.numberOfDays,
        Subtotal: subtotal,
        Discount_Amount: discount,
        California_Tax: tax,
        Total_Amount: total,
        added: new Date().toISOString()
      };
      const data = {
        appname: 'tkd-score',
        formname: 'Gen_3_Scoring_Rental',
        params: JSON.stringify(zohoData)
      };
      await callWFAPI(0, 'zohoCreator', data);
      setSubmitSuccess(true);
      setCustomerInfo({
        ...initialCustomerInfo,
        email: globalEmail || ''
      });
      setSubmitted(false);
      setErrors({});
      setOwnEquipmentAcknowledged(false);
      setNoHeadGearConfirmed(false);
      if (onRentalSubmitted) {
        onRentalSubmitted();
      }
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form to Zoho Creator:', error);
      const errorMessage = error.response?.data?.message || 'There was an error submitting your request to our database. Please try again or contact us directly.';
      setSubmitError(errorMessage);
      if (onRentalSubmitted) {
        onRentalSubmitted();
      }
      setSubmitSuccess(true);
      setCustomerInfo({
        ...initialCustomerInfo,
        email: globalEmail || ''
      });
      setSubmitted(false);
      setErrors({});
      setOwnEquipmentAcknowledged(false);
      setNoHeadGearConfirmed(false);
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmit = async () => {
    setSubmitted(true);
    if (validateForm()) {
      // Check if customer only selected technician with no equipment
      if (technicianOnlySelected && !ownEquipmentAcknowledged) {
        setShowOwnEquipmentModal(true);
        return;
      }
      // Check if customer has Full Ring but no Head gear
      if (hasFullRingWithoutHead && !noHeadGearConfirmed) {
        setShowNoHeadGearModal(true);
        return;
      }
      await proceedWithSubmission();
    }
  };
  const handleOwnEquipmentAcknowledge = async () => {
    setOwnEquipmentAcknowledged(true);
    setShowOwnEquipmentModal(false);
    await proceedWithSubmission();
  };
  const handleNoHeadGearConfirm = async () => {
    setNoHeadGearConfirmed(true);
    setShowNoHeadGearModal(false);
    await proceedWithSubmission();
  };
  const handleViewPreviousRentals = () => {
    setShowPreviousRentalsModal(false);
    // Trigger expansion of PendingRentals component
    if (onRentalSubmitted) {
      onRentalSubmitted(true); // Pass true to indicate should expand
    }
  };
  // Helper function to get the reason why submit is disabled
  const getSubmitDisabledReason = () => {
    if (!hasSelections) {
      return 'Please select equipment or services to continue';
    }
    if (techRequired && !selections.requireTech) {
      return 'On-site technician is required for Gen 3 rentals';
    }
    if (submitted && Object.keys(errors).length > 0) {
      const errorFields = Object.keys(errors);
      if (errorFields.length === 1) {
        return `Please fix the error in: ${errorFields[0]}`;
      }
      return `Please fix errors in: ${errorFields.join(', ')}`;
    }
    return null;
  };
  if (submitSuccess) {
    return <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1486">
        <div className="text-center py-8" data-id="element-1487">
          <CheckCircleIcon size={48} className="mx-auto text-green-500 mb-4" data-id="element-1488" />
          <h2 className="text-2xl font-semibold mb-2 text-green-800" data-id="element-1489">
            Request Submitted!
          </h2>
          <p className="text-slate-600" data-id="element-1490">
            Thank you for your rental request. A representative will contact you
            shortly to confirm details.
          </p>
        </div>
      </div>;
  }
  return <>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md lg:sticky lg:top-6" data-id="element-1491">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 text-blue-800" data-id="element-1492">
          Order Summary
        </h2>

        {!hasSelections ? <div className="text-slate-500 text-center py-6" data-id="element-1493">
            No items selected yet
          </div> : <div className="space-y-4" data-id="element-1494">
            {/* Day 1 Equipment */}
            {selections.gen3Body > 0 && <div className="flex justify-between" data-id="element-1495">
                <div data-id="element-1496">
                  <p className="font-medium" data-id="element-1497">
                    {isGen2Mode ? 'Gen 2' : 'Gen 3'} Body
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-1498">
                    {selections.gen3Body} × $
                    {(prices.gen3Body * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                  </p>
                </div>
                <p className="font-medium" data-id="element-1499">
                  $
                  {(selections.gen3Body * prices.gen3Body * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                </p>
              </div>}
            {selections.gen3Head > 0 && <div className="flex justify-between" data-id="element-1500">
                <div data-id="element-1501">
                  <p className="font-medium" data-id="element-1502">
                    {isGen2Mode ? 'Gen 2' : 'Gen 3'} Head
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-1503">
                    {selections.gen3Head} × $
                    {(prices.gen3Head * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                  </p>
                </div>
                <p className="font-medium" data-id="element-1504">
                  $
                  {(selections.gen3Head * prices.gen3Head * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                </p>
              </div>}
            {selections.gen3JudgesOnly > 0 && <div className="flex justify-between" data-id="element-1505">
                <div data-id="element-1506">
                  <p className="font-medium" data-id="element-1507">
                    {isGen2Mode ? 'Gen 2' : 'Gen 3'} Judges Only
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-1508">
                    {selections.gen3JudgesOnly} × $
                    {(prices.gen3JudgesOnly * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                  </p>
                </div>
                <p className="font-medium" data-id="element-1509">
                  $
                  {(selections.gen3JudgesOnly * prices.gen3JudgesOnly * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                </p>
              </div>}
            {selections.fullGen3Ring > 0 && <div className="flex justify-between" data-id="element-1510">
                <div data-id="element-1511">
                  <p className="font-medium" data-id="element-1512">
                    Full {isGen2Mode ? 'Gen 2' : 'Gen 3'} Ring
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-1513">
                    {selections.fullGen3Ring} × $
                    {(prices.fullGen3Ring * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                  </p>
                </div>
                <p className="font-medium" data-id="element-1514">
                  $
                  {(selections.fullGen3Ring * prices.fullGen3Ring * (isGen2Mode ? 0.7 : 1)).toFixed(2)}
                </p>
              </div>}

            {/* Additional Days Equipment (50% price per day) */}
            {selections.numberOfDays > 1 && <>
                <div className="border-t border-blue-200 pt-3 mt-3" data-id="element-1515">
                  <p className="text-sm font-semibold text-blue-700 mb-2" data-id="element-1516">
                    Days 2-{selections.numberOfDays} Equipment (50% price per
                    day)
                  </p>
                </div>
                {selections.gen3Body > 0 && <div className="flex justify-between" data-id="element-1517">
                    <div data-id="element-1518">
                      <p className="font-medium" data-id="element-1519">
                        {isGen2Mode ? 'Gen 2' : 'Gen 3'} Body (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1520">
                        {selections.gen3Body} × $
                        {(prices.gen3Body * (isGen2Mode ? 0.7 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1521">
                      $
                      {(selections.gen3Body * prices.gen3Body * (isGen2Mode ? 0.7 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
                {selections.gen3Head > 0 && <div className="flex justify-between" data-id="element-1522">
                    <div data-id="element-1523">
                      <p className="font-medium" data-id="element-1524">
                        {isGen2Mode ? 'Gen 2' : 'Gen 3'} Head (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1525">
                        {selections.gen3Head} × $
                        {(prices.gen3Head * (isGen2Mode ? 0.7 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1526">
                      $
                      {(selections.gen3Head * prices.gen3Head * (isGen2Mode ? 0.7 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
                {selections.gen3JudgesOnly > 0 && <div className="flex justify-between" data-id="element-1527">
                    <div data-id="element-1528">
                      <p className="font-medium" data-id="element-1529">
                        {isGen2Mode ? 'Gen 2' : 'Gen 3'} Judges Only (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1530">
                        {selections.gen3JudgesOnly} × $
                        {(prices.gen3JudgesOnly * (isGen2Mode ? 0.7 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1531">
                      $
                      {(selections.gen3JudgesOnly * prices.gen3JudgesOnly * (isGen2Mode ? 0.7 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
                {selections.fullGen3Ring > 0 && <div className="flex justify-between" data-id="element-1532">
                    <div data-id="element-1533">
                      <p className="font-medium" data-id="element-1534">
                        Full {isGen2Mode ? 'Gen 2' : 'Gen 3'} Ring (Days 2-
                        {selections.numberOfDays})
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1535">
                        {selections.fullGen3Ring} × $
                        {(prices.fullGen3Ring * (isGen2Mode ? 0.7 : 1) * 0.5).toFixed(2)}{' '}
                        × {selections.numberOfDays - 1} days
                      </p>
                    </div>
                    <p className="font-medium" data-id="element-1536">
                      $
                      {(selections.fullGen3Ring * prices.fullGen3Ring * (isGen2Mode ? 0.7 : 1) * 0.5 * (selections.numberOfDays - 1)).toFixed(2)}
                    </p>
                  </div>}
              </>}

            {selections.requireTech && <div className="flex justify-between py-2 bg-red-50 px-3 rounded-md border border-red-100" data-id="element-1537">
                <span className="font-medium text-red-800" data-id="element-1538">
                  On-site Technician {isGen2Mode ? '(Optional)' : '(Required)'}
                  {selections.numberOfDays > 1 ? ` - ${selections.numberOfDays} Days` : ''}
                </span>
                <span className="font-semibold text-red-800" data-id="element-1539">
                  ${(prices.technician * selections.numberOfDays).toFixed(2)}
                </span>
              </div>}

            {!selections.requireTech && hasSelections && techRequired && <div className="bg-red-50 border border-red-200 rounded-md p-3" data-id="element-1540">
                <div className="flex items-start" data-id="element-1541">
                  <AlertTriangle size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1542" />
                  <p className="text-sm text-red-700" data-id="element-1543">
                    <strong data-id="element-1544">Required:</strong> You must select an on-site
                    technician to complete your Gen 3 rental request.
                  </p>
                </div>
              </div>}

            {(discountSelections.collegiate || discountSelections.usatSanctioned) && <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-1545">
                <p className="font-medium text-green-700 mb-2" data-id="element-1546">
                  Discount Requests:
                </p>
                <div className="space-y-2" data-id="element-1547">
                  {discountSelections.usatSanctioned && <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3" data-id="element-1548">
                      <div className="flex items-center justify-between" data-id="element-1549">
                        <div className="flex items-center text-green-800" data-id="element-1550">
                          <CheckCircleIcon size={16} className="mr-2 flex-shrink-0" data-id="element-1551" />
                          <span className="font-semibold" data-id="element-1552">
                            USAT Sanctioned Event
                          </span>
                        </div>
                        <span className="bg-green-600 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-1553">
                          15% OFF
                        </span>
                      </div>
                    </div>}
                  {discountSelections.collegiate && <div className="flex items-center text-sm text-green-700" data-id="element-1554">
                      <CheckCircleIcon size={14} className="mr-2" data-id="element-1555" />
                      Collegiate Discount (10%)
                    </div>}
                </div>
                <p className="text-xs text-slate-500 mt-2" data-id="element-1556">
                  These will be reviewed by a representative
                </p>
              </div>}

            <div className="border-t border-slate-200 pt-4" data-id="element-1557">
              <div className="flex justify-between" data-id="element-1558">
                <p className="font-medium" data-id="element-1559">Subtotal</p>
                <p className="font-medium" data-id="element-1560">${subtotal.toFixed(2)}</p>
              </div>
            </div>

            {discount > 0 && <div className="flex justify-between text-green-700" data-id="element-1561">
                <div data-id="element-1562">
                  <p className="font-semibold" data-id="element-1563">
                    {discountSelections.usatSanctioned ? 'USAT Sanctioned Discount (15%)' : 'Collegiate Discount (10%)'}
                  </p>
                </div>
                <p className="font-semibold" data-id="element-1564">-${discount.toFixed(2)}</p>
              </div>}

            {tax > 0 && <div className="flex justify-between text-red-700" data-id="element-1565">
                <div data-id="element-1566">
                  <p className="font-medium" data-id="element-1567">
                    California Use Tax ({(prices.taxRate * 100).toFixed(0)}%)
                  </p>
                  <p className="text-xs" data-id="element-1568">
                    Applied to California shipping addresses
                  </p>
                </div>
                <p className="font-medium" data-id="element-1569">${tax.toFixed(2)}</p>
              </div>}

            <div className="border-t border-slate-200 pt-4 mt-4" data-id="element-1570">
              <div className="flex justify-between font-bold text-lg" data-id="element-1571">
                <p data-id="element-1572">Total</p>
                <p data-id="element-1573">${total.toFixed(2)}</p>
              </div>
            </div>
          </div>}

        {hasSelections && <div className="mt-6 border-t border-slate-200 pt-6" data-id="element-1574">
            <h3 className="font-medium text-lg mb-4 text-blue-800" data-id="element-1575">
              Customer Information
            </h3>
            <div className="space-y-4" data-id="element-1576">
              <div data-id="element-1577">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1578">
                  Name <span className="text-red-500" data-id="element-1579">*</span>
                </label>
                <input type="text" name="name" value={customerInfo.name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="Full Name" data-id="element-1580" />
                {submitted && errors.name && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1581">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1582" />
                    {errors.name}
                  </p>}
              </div>

              <div data-id="element-1583">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1584">
                  Phone <span className="text-red-500" data-id="element-1585">*</span>
                </label>
                <input type="tel" name="phone" value={customerInfo.phone} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.phone ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="Phone Number" data-id="element-1586" />
                {submitted && errors.phone && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1587">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1588" />
                    {errors.phone}
                  </p>}
              </div>

              <div data-id="element-1589">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1590">
                  Email <span className="text-red-500" data-id="element-1591">*</span>
                </label>
                <div className="relative" data-id="element-1592">
                  <input type="email" name="email" value={customerInfo.email} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.email ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} placeholder="Email Address" data-id="element-1593" />
                  {isCheckingEmail && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1594">
                      <LoaderIcon size={16} className="animate-spin text-blue-600" data-id="element-1595" />
                    </div>}
                </div>
                {submitted && errors.email && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1596">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1597" />
                    {errors.email}
                  </p>}
              </div>

              <div data-id="element-1598">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1599">
                  Address <span className="text-red-500" data-id="element-1600">*</span>
                </label>
                <input type="text" name="address" value={customerInfo.address} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Street Address" data-id="element-1601" />
              </div>

              <div data-id="element-1602">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1603">
                  Address 2
                </label>
                <input type="text" name="address2" value={customerInfo.address2} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Apt, Suite, Unit, etc. (optional)" data-id="element-1604" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1605">
                <div data-id="element-1606">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1607">
                    City <span className="text-red-500" data-id="element-1608">*</span>
                  </label>
                  <input type="text" name="city" value={customerInfo.city} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="City" data-id="element-1609" />
                </div>

                <div data-id="element-1610">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1611">
                    State <span className="text-red-500" data-id="element-1612">*</span>
                  </label>
                  <select name="state" value={customerInfo.state} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base" data-id="element-1613">
                    <option value="" data-id="element-1614">Select State</option>
                    <option value="AL" data-id="element-1615">Alabama</option>
                    <option value="AK" data-id="element-1616">Alaska</option>
                    <option value="AZ" data-id="element-1617">Arizona</option>
                    <option value="AR" data-id="element-1618">Arkansas</option>
                    <option value="CA" data-id="element-1619">California</option>
                    <option value="CO" data-id="element-1620">Colorado</option>
                    <option value="CT" data-id="element-1621">Connecticut</option>
                    <option value="DE" data-id="element-1622">Delaware</option>
                    <option value="FL" data-id="element-1623">Florida</option>
                    <option value="GA" data-id="element-1624">Georgia</option>
                    <option value="HI" data-id="element-1625">Hawaii</option>
                    <option value="ID" data-id="element-1626">Idaho</option>
                    <option value="IL" data-id="element-1627">Illinois</option>
                    <option value="IN" data-id="element-1628">Indiana</option>
                    <option value="IA" data-id="element-1629">Iowa</option>
                    <option value="KS" data-id="element-1630">Kansas</option>
                    <option value="KY" data-id="element-1631">Kentucky</option>
                    <option value="LA" data-id="element-1632">Louisiana</option>
                    <option value="ME" data-id="element-1633">Maine</option>
                    <option value="MD" data-id="element-1634">Maryland</option>
                    <option value="MA" data-id="element-1635">Massachusetts</option>
                    <option value="MI" data-id="element-1636">Michigan</option>
                    <option value="MN" data-id="element-1637">Minnesota</option>
                    <option value="MS" data-id="element-1638">Mississippi</option>
                    <option value="MO" data-id="element-1639">Missouri</option>
                    <option value="MT" data-id="element-1640">Montana</option>
                    <option value="NE" data-id="element-1641">Nebraska</option>
                    <option value="NV" data-id="element-1642">Nevada</option>
                    <option value="NH" data-id="element-1643">New Hampshire</option>
                    <option value="NJ" data-id="element-1644">New Jersey</option>
                    <option value="NM" data-id="element-1645">New Mexico</option>
                    <option value="NY" data-id="element-1646">New York</option>
                    <option value="NC" data-id="element-1647">North Carolina</option>
                    <option value="ND" data-id="element-1648">North Dakota</option>
                    <option value="OH" data-id="element-1649">Ohio</option>
                    <option value="OK" data-id="element-1650">Oklahoma</option>
                    <option value="OR" data-id="element-1651">Oregon</option>
                    <option value="PA" data-id="element-1652">Pennsylvania</option>
                    <option value="RI" data-id="element-1653">Rhode Island</option>
                    <option value="SC" data-id="element-1654">South Carolina</option>
                    <option value="SD" data-id="element-1655">South Dakota</option>
                    <option value="TN" data-id="element-1656">Tennessee</option>
                    <option value="TX" data-id="element-1657">Texas</option>
                    <option value="UT" data-id="element-1658">Utah</option>
                    <option value="VT" data-id="element-1659">Vermont</option>
                    <option value="VA" data-id="element-1660">Virginia</option>
                    <option value="WA" data-id="element-1661">Washington</option>
                    <option value="WV" data-id="element-1662">West Virginia</option>
                    <option value="WI" data-id="element-1663">Wisconsin</option>
                    <option value="WY" data-id="element-1664">Wyoming</option>
                  </select>
                </div>
              </div>

              <div data-id="element-1665">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1666">
                  ZIP Code <span className="text-red-500" data-id="element-1667">*</span>
                </label>
                <input type="text" name="zip" value={customerInfo.zip} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="ZIP Code" maxLength={10} data-id="element-1668" />
                {tax > 0 && <p className="text-red-600 text-xs mt-1" data-id="element-1669">
                    California shipping address:{' '}
                    {(prices.taxRate * 100).toFixed(0)}% use tax will be applied
                  </p>}
              </div>

              <div data-id="element-1670">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1671">
                  Event Name
                </label>
                <input type="text" name="eventName" value={customerInfo.eventName} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Name of Event" data-id="element-1672" />
              </div>

              <div data-id="element-1673">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1674">
                  Event Venue City/State
                </label>
                <input type="text" name="eventVenueCityState" value={customerInfo.eventVenueCityState} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Los Angeles, CA" data-id="element-1675" />
              </div>

              <div data-id="element-1676">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1677">
                  Proposed Event Date <span className="text-red-500" data-id="element-1678">*</span>
                </label>
                <input type="date" name="proposedEventDate" value={customerInfo.proposedEventDate} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.proposedEventDate ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} data-id="element-1679" />
                {submitted && errors.proposedEventDate && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1680">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1681" />
                    {errors.proposedEventDate}
                  </p>}
              </div>

              <div data-id="element-1682">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1683">
                  Alternate Date{' '}
                  {!customerInfo.proposedEventDate && <span className="text-red-500" data-id="element-1684">*</span>}
                </label>
                <input type="date" name="alternateDate" value={customerInfo.alternateDate} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md ${errors.alternateDate ? 'border-red-500' : 'border-slate-300'} focus:ring-blue-500 focus:border-blue-500`} data-id="element-1685" />
                {submitted && errors.alternateDate && <p className="text-red-500 text-xs mt-1 flex items-center" data-id="element-1686">
                    <AlertCircleIcon size={12} className="mr-1" data-id="element-1687" />
                    {errors.alternateDate}
                  </p>}
                {!customerInfo.proposedEventDate && !customerInfo.alternateDate && <p className="text-slate-500 text-xs mt-1" data-id="element-1688">
                      At least one future date is required
                    </p>}
              </div>

              <div data-id="element-1689">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1690">
                  Preferred Technician
                </label>
                <select name="preferredTechnician" value={customerInfo.preferredTechnician} onChange={handleInputChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1691">
                  <option value="" data-id="element-1692">Select a technician...</option>
                  {technicianOptions.map(tech => <option key={tech.id} value={tech.id} data-id="element-1693">
                      {tech.name}
                    </option>)}
                </select>
              </div>

              <div data-id="element-1694">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1695">
                  Notes
                </label>
                <textarea name="notes" value={customerInfo.notes} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Additional information or special requests" data-id="element-1696" />
              </div>
            </div>
          </div>}

        <div className="mt-6" data-id="element-1697">
          <button type="button" disabled={!hasSelections || isSubmitting || techRequired && !selections.requireTech} onClick={handleSubmit} className={`w-full py-3 px-4 rounded-md flex justify-center items-center ${hasSelections && !isSubmitting && (!techRequired || selections.requireTech) ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`} data-id="element-1698">
            {isSubmitting ? <>
                <LoaderIcon size={20} className="mr-2 animate-spin" data-id="element-1699" />
                Submitting...
              </> : <>
                <CheckCircleIcon size={20} className="mr-2" data-id="element-1700" />
                Complete Rental Request
              </>}
          </button>

          {/* Show specific error message when button is disabled */}
          {(!hasSelections || techRequired && !selections.requireTech || submitted && Object.keys(errors).length > 0) && !isSubmitting && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1701">
                <div className="flex items-start" data-id="element-1702">
                  <AlertCircleIcon size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1703" />
                  <p className="text-sm text-red-700 font-medium" data-id="element-1704">
                    {getSubmitDisabledReason()}
                  </p>
                </div>
              </div>}
        </div>

        {submitError && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-id="element-1705">
            {submitError}
          </div>}

        <div className="mt-4 text-xs text-slate-500 text-center" data-id="element-1706">
          A representative will contact you to confirm your order and arrange
          payment.
        </div>
      </div>

      {/* No Head Gear Confirmation Modal */}
      {showNoHeadGearModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1707">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" data-id="element-1708">
            <div className="p-6" data-id="element-1709">
              <div className="flex items-start mb-4" data-id="element-1710">
                <AlertTriangle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1711" />
                <div data-id="element-1712">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-1713">
                    No E-Head Gear Selected
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1714">
                    You've selected Full Gen 3 Ring equipment but haven't added
                    any Gen 3 Head gear.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-id="element-1715">
                <p className="text-sm text-blue-800 mb-2" data-id="element-1716">
                  <strong data-id="element-1717">Please confirm:</strong>
                </p>
                <p className="text-xs text-blue-800" data-id="element-1718">
                  You do not need e-head gear for this event. The Full Gen 3
                  Ring includes body sensors and judges only (head gear not
                  included).
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1719">
                <button onClick={() => {
              setShowNoHeadGearModal(false);
              setSubmitted(false);
            }} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1720">
                  Go Back
                </button>
                <button onClick={handleNoHeadGearConfirm} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1721">
                  Confirm - No Head Gear
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Previous Rentals Found Modal */}
      {showPreviousRentalsModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1722">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" data-id="element-1723">
            <div className="p-6" data-id="element-1724">
              <div className="flex items-start mb-4" data-id="element-1725">
                <Package className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1726" />
                <div data-id="element-1727">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-1728">
                    Previous Rental Requests Found
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1729">
                    You have {previousRentalsCount} previous rental request
                    {previousRentalsCount !== 1 ? 's' : ''} associated with this
                    email address.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-id="element-1730">
                <p className="text-xs text-blue-800" data-id="element-1731">
                  Your previous rental requests will be displayed above. You can
                  view their status and details at any time.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1732">
                <button onClick={() => setShowPreviousRentalsModal(false)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1733">
                  Close
                </button>
                <button onClick={handleViewPreviousRentals} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1734">
                  View Requests
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Own Equipment Acknowledgment Modal */}
      {showOwnEquipmentModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1735">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" data-id="element-1736">
            <div className="p-6" data-id="element-1737">
              <div className="flex items-start mb-4" data-id="element-1738">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" data-id="element-1739" />
                <div data-id="element-1740">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" data-id="element-1741">
                    Own Equipment Acknowledgment
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-1742">
                    I have my own generation 3 Daedo Equipment and require
                    approval to use it at a sanctioned event.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4" data-id="element-1743">
                <p className="text-xs text-yellow-800" data-id="element-1744">
                  By acknowledging this, you confirm that you own Gen 3 Daedo
                  equipment and understand that approval is required to use it
                  at sanctioned events. A representative will contact you to
                  verify and approve your equipment.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1745">
                <button onClick={() => {
              setShowOwnEquipmentModal(false);
              setSubmitted(false);
            }} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1746">
                  Cancel
                </button>
                <button onClick={handleOwnEquipmentAcknowledge} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1747">
                  I Acknowledge
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}